import React, {useEffect, useState, useMemo, useCallback} from "react";
import {getAllProjects} from "../../services/projects";
import ProjectsFiles from "../../components/ProductFiles/ProjectsFiles";
import Pager from "../../components/Pager";
import ProjectItem from "../../components/ProductFiles/ProjectsFiles/ProjectItem/ProjectItem";
import {getCookieValue} from "../../utils/cookieUtils";

import "./MyLibrary.scss";

const MyLibrary = () => {
    const [token, setToken] = useState(null);
    const [singleProjects, setSingleProjects] = useState([]);
    const [singleStreamProjects, setSingleStreamProjects] = useState([]);
    const [subscriptionsProjects, setSubscriptionProjects] = useState([]);
    const [subscriptionCancel, setSubscriptionCancel] = useState("");
    const [subscriptionsStreamProjects, setSubscriptionStreamProjects] = useState([]);
    const [activeProjects, setActiveProjects] = useState("single_payment");
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        total: "",
    });

    const getProjects = useMemo(() => {
        return async (token, paymentType, page, pageSize, streaming) => {
            try {
                return await getAllProjects({
                    token: token,
                    paymentType: paymentType,
                    page: page,
                    pageSize: pageSize,
                    ...(streaming !== undefined && {streaming})
                });
            } catch (error) {
                console.error("Error fetching projects:", error);
            }
        };
    }, []);

    const refreshProjects = useCallback(async () => {
        if (token) {
            try {
                const singleStreamProjects = await getProjects(token, "single_payment", "1", "1000", true);
                setSingleStreamProjects(singleStreamProjects.data);

                const singleProjects = await getProjects(token, "single_payment", "1", "25", false);
                setSingleProjects(singleProjects);
                setPagination((prevPagination) => ({
                    ...prevPagination,
                    total: singleProjects.meta.total,
                }));

                const subscriptionsStreamProjects = await getProjects(token, "subscription", "1", "1000", true);
                setSubscriptionStreamProjects(subscriptionsStreamProjects.data);

                const subscriptionsProjects = await getProjects(token, "subscription", "1", "25", false);
                setSubscriptionProjects(subscriptionsProjects);
            } catch (error) {
                console.error("Error fetching projects:", error);
            }
        }
    }, [token, getProjects]);

    const handleClick = async (pageNumber) => {
        const projects = await getProjects(token, activeProjects, pageNumber, pagination.pageSize, false);
        setPagination((prevPagination) => ({
            ...prevPagination,
            page: pageNumber,
        }));
        if (activeProjects === "single_payment") {
            setSingleProjects(projects);
        } else {
            setSubscriptionProjects(projects);
        }
    };

    useEffect(() => {
        const homeAccTokenValue = getCookieValue("home_ACC_TOKEN");
        if (homeAccTokenValue) {
            setToken(homeAccTokenValue);
        }
    }, []);

    useEffect(() => {
        refreshProjects();
    }, [token, refreshProjects]);

    const handleActiveProjectsClick = (activeProjects) => {
        setActiveProjects(activeProjects);
        if (activeProjects === "single_payment") {
            setPagination(() => ({
                page: 1,
                pageSize: 25,
                total: singleProjects.meta.total,
            }));
        } else {
            setPagination(() => ({
                page: 1,
                pageSize: 25,
                total: subscriptionsProjects.meta.total,
            }));
        }
    };

    useEffect(() => {
        if (subscriptionCancel) {
            const timer = setTimeout(() => {
                setSubscriptionCancel("");
            }, 60000);

            return () => clearTimeout(timer);
        }
    }, [subscriptionCancel]);

    const handleCloseWarning = () => {
        setSubscriptionCancel("");
    };

    return (
        <section className="section-content">
            <div className="content">
                {subscriptionCancel && (
                    <div className="warning">
                        {subscriptionCancel}
                        <button className="close-warning" onClick={handleCloseWarning}>✕</button>
                    </div>
                )}
                <div className="breadcrumb">
                    <a href="/account">My Homepage</a> »
                    <a href="/projects" className="last">
                        {activeProjects === "single_payment" ? " My Library" : " Subscriptions"}
                    </a>
                </div>
                <ul className="list-tab">
                    <li>
                        <button
                            disabled={!singleProjects || !singleProjects.meta}
                            className={activeProjects === "single_payment" ? "active" : ""}
                            onClick={() => handleActiveProjectsClick("single_payment")}
                        >
                            Single payment
                            ({singleProjects && singleProjects.meta && Number(singleProjects.meta.total) + Number(singleStreamProjects.length)})
                        </button>
                    </li>
                    <li>
                        <button
                            disabled={!subscriptionsProjects || !subscriptionsProjects.meta}
                            className={activeProjects !== "single_payment" ? "active" : ""}
                            onClick={() => handleActiveProjectsClick("subscriptions")}
                        >
                            Subscriptions
                            ({subscriptionsProjects && subscriptionsProjects.meta && subscriptionsProjects.meta.total + subscriptionsStreamProjects.length})
                        </button>
                    </li>
                </ul>

                <div className="product-files">
                    <div className="product-files--files">
                        <h2>Streams</h2>
                        <div className="streams-item">
                            {activeProjects === "single_payment" && Array.isArray(singleStreamProjects) &&
                                singleStreamProjects.map((project) => (
                                    <ProjectItem key={project.id} project={project} activeProjects={activeProjects}
                                                 token={token} setSubscriptionCancel={setSubscriptionCancel} refreshProjects={refreshProjects} />
                                ))
                            }
                            {activeProjects !== "single_payment" && Array.isArray(subscriptionsStreamProjects) &&
                                subscriptionsStreamProjects.map((project) => (
                                    <ProjectItem key={project.id} project={project} activeProjects={activeProjects}
                                                 token={token} setSubscriptionCancel={setSubscriptionCancel} refreshProjects={refreshProjects} />
                                ))
                            }
                        </div>
                    </div>
                </div>


                {singleProjects && subscriptionsProjects && pagination && (
                    <>
                        <ProjectsFiles
                            projects={activeProjects === "single_payment" ? singleProjects : subscriptionsProjects}
                            activeProjects={activeProjects}
                            token={token}
                            setSubscriptionCancel={setSubscriptionCancel}
                            refreshProjects={refreshProjects}
                        />
                        <Pager
                            total={pagination.total}
                            itemsPerPage={pagination.pageSize}
                            currentPage={pagination.page}
                            handleClick={handleClick}
                        />
                    </>
                )}
            </div>
        </section>
    );
};

export default MyLibrary;